/**
 * Shoulder evidence — the engine-side half of the 'cap-height' recentre:
 * gather the upper-arm SEGMENT's bind-pose flesh and hand it to the kernel's
 * pure `shoulderCapSeat`. The kernel decides; this only fetches.
 *
 * The segment is the shoulder-seat probe's, verbatim: every vertex whose
 * DOMINANT influence is the Upperarm or a bone hanging from it that is not
 * under the Forearm — the Tripo twist supplements (UpperarmTwist01/02) and a
 * grafted BicepLeaf included. Weight-filtered on purpose, unlike the elbow's
 * limb-axis capture: the question here is "where does the ARM's own flesh
 * begin and end around the pivot", and the clavicle-owned chest that shares
 * the region is exactly what the cap must not include.
 *
 * Vertices are placed by the mesh's `bindMatrix` only — the pose-independent
 * bind-world frame the IBM-derived joints live in (elbow-evidence's lesson).
 *
 * Depends on: three, kernel. Depended on by: run-document's recentre door,
 * the shoulder probes.
 */
import { Vector3, type SkinnedMesh } from 'three';
import { subtreeOf, type RigDescriptor, type Vec3Data } from '@harryhausen/kernel';

/** The rig bones whose flesh is the upper-arm segment of `upperArm` (child `forearm` excluded with its subtree). */
export function upperArmSegmentBones(rig: RigDescriptor, upperArm: string, forearm: string): Set<string> {
  const below = new Set(subtreeOf(rig, forearm));
  return new Set(subtreeOf(rig, upperArm).filter((n) => !below.has(n)));
}

/**
 * Bind-world positions of every vertex, across the given skins (the SELECTED
 * skeleton's group — a VRoid body is 14–20 skins on one armature), whose
 * dominant influence is in `bones`.
 */
export function dominantFleshOf(meshes: readonly SkinnedMesh[], bones: ReadonlySet<string>): Vec3Data[] {
  const out: Vec3Data[] = [];
  const v = new Vector3();
  for (const mesh of meshes) {
    const position = mesh.geometry.getAttribute('position');
    const si = mesh.geometry.getAttribute('skinIndex');
    const sw = mesh.geometry.getAttribute('skinWeight');
    if (si === undefined || sw === undefined) continue;
    const names = mesh.skeleton.bones.map((b) => b.name);
    for (let i = 0; i < position.count; i += 1) {
      let best = -1;
      let bestW = 0;
      for (let k = 0; k < si.itemSize; k += 1) {
        const w = sw.getComponent(i, k);
        if (w > bestW) {
          bestW = w;
          best = si.getComponent(i, k);
        }
      }
      if (best < 0 || !bones.has(names[best] ?? '')) continue;
      v.fromBufferAttribute(position, i).applyMatrix4(mesh.bindMatrix);
      out.push({ x: v.x, y: v.y, z: v.z });
    }
  }
  return out;
}
